import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Calendar, Euro, MapPin } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

export const Vehicles: React.FC = () => {
  const { city } = useAppContext();
  
  const recentPurchases = [
    {
      id: 1,
      brand: 'Audi',
      model: 'A4 2019',
      image: '/img/vehicles/audi.png',
      price: '€28,500',
      location: 'Piacenza',
      date: '2 giorni fa',
      overlay: '/img/overlay1.png'
    },
    {
      id: 2,
      brand: 'Fiat',
      model: '500 2020',
      image: '/img/vehicles/fiat-500.png',
      price: '€15,200',
      location: 'Milano',
      date: '1 settimana fa',
      overlay: '/img/overlay2.png'
    },
    {
      id: 3,
      brand: 'Opel',
      model: 'Corsa 2021',
      image: '/img/vehicles/opel-corsa.png',
      price: '€18,800',
      location: 'Parma',
      date: '3 giorni fa',
      overlay: '/img/overlay1.png'
    },
    {
      id: 4,
      brand: 'Volkswagen',
      model: 'Polo 2020',
      image: '/img/vehicles/polo.png',
      price: '€16,750',
      location: 'Torino',
      date: '1 settimana fa',
      overlay: '/img/overlay2.png'
    }
  ];

  return (
    <section className="relative py-20 overflow-hidden bg-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute w-20 h-20 bg-blue-600 rounded-full top-10 left-10"></div>
        <div className="absolute w-16 h-16 bg-yellow-500 rounded-full top-40 right-20"></div>
        <div className="absolute w-12 h-12 bg-blue-600 rounded-full bottom-20 left-1/4"></div>
      </div>

      <div className="container relative px-4 mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <span className="text-[#fdc751] font-semibold text-lg mb-2 block">VEICOLI</span>
          <h2 className="mb-4 text-4xl font-bold text-gray-900">
            {city ? `Ultimi Acquisti a ${city}` : 'Ultimi Acquisti'}
          </h2>
          <p className="max-w-3xl mx-auto text-xl text-gray-600">
            {city 
              ? `Ecco alcuni degli ultimi veicoli che abbiamo acquistato dai nostri clienti soddisfatti a ${city}`
              : 'Ecco alcuni degli ultimi veicoli che abbiamo acquistato dai nostri clienti soddisfatti'
            }
          </p>
        </motion.div>

        {/* Vehicles Grid */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {recentPurchases.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative overflow-hidden transition-all duration-300 bg-white shadow-lg group rounded-2xl hover:shadow-2xl"
            >
              {/* Vehicle Image with Overlay */}
              <div className="relative h-48 overflow-hidden rounded-t-2xl">
                {/* Background Overlay */}
                <div 
                  className="absolute inset-0 bg-center bg-cover opacity-20"
                  style={{ backgroundImage: `url(${vehicle.overlay})` }}
                ></div>
                
                {/* Vehicle Image */}
                <div className="relative z-10 flex items-center justify-center h-full p-4">
                  <img
                    src={vehicle.image}
                    alt={`${vehicle.brand} ${vehicle.model}`}
                    className="object-contain max-w-full max-h-full transition-transform duration-300 drop-shadow-lg group-hover:scale-110"
                  />
                </div>

                {/* Purchase Badge */}
                <div className="absolute z-20 top-4 right-4">
                  <div className="flex items-center px-3 py-1 text-xs font-semibold text-white bg-green-500 rounded-full">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Acquistato
                  </div>
                </div>
              </div>

              {/* Vehicle Info */}
              <div className="p-6">
                <h3 className="mb-2 text-xl font-bold text-gray-900">
                  {vehicle.brand} {vehicle.model}
                </h3>
                
                <div className="space-y-3">
                {/*  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-[#fdc751]">{vehicle.price}</span>
                  </div> */}
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="w-4 h-4 mr-2" />
                    <span>{vehicle.location}</span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{vehicle.date}</span>
                  </div>
                </div>
              </div>

              {/* Hover Effect Border */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#fdc751] rounded-2xl transition-colors duration-300"></div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="p-8 bg-gradient-to-r from-blue-50 to-yellow-50 rounded-2xl">
            <h3 className="mb-4 text-2xl font-bold text-gray-900">
              {city ? `Vuoi vendere anche tu la tua auto a ${city}?` : 'Vuoi vendere anche tu la tua auto?'}
            </h3>
            <p className="max-w-2xl mx-auto mb-6 text-gray-600">
              {city 
                ? `Unisciti ai nostri clienti soddisfatti a ${city} e ottieni il miglior prezzo per il tuo veicolo. Valutazione gratuita e pagamento immediato garantiti.`
                : 'Unisciti ai nostri clienti soddisfatti e ottieni il miglior prezzo per il tuo veicolo. Valutazione gratuita e pagamento immediato garantiti.'
              }
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const heroSection = document.getElementById('hero-section');
                if (heroSection) {
                  heroSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="px-8 py-4 bg-[#fdc751] text-gray-900 rounded-lg font-semibold hover:bg-[#fdc751]/90 transition-colors inline-flex items-center"
            >
              <Euro className="w-5 h-5 mr-2" />
              {city ? `Ottieni la Tua Valutazione a ${city}` : 'Ottieni la Tua Valutazione'}
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}; 